import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'

import { CheckMarkIcon } from '../../icons'
import Loader from './loader'

export interface CheckboxProps {
  /**
   * Checkbox label
   * */
  label?: string

  /**
   * Loading state
   * */
  loading?: boolean

  /**
   * Checkbox label as a react node
   * */
  component?: React.ReactNode

  /**
   * Is the checkbox checked?
   * */
  checked?: boolean

  /**
   * Class name for the wrapper
   * */
  wrapperClassName?: string

  /**
   * disabled the checkbox
   * */
  disabled?: boolean

  /**
   * label variant
   * */
  labelVariant?: 'default' | 'small'

  /**
   * label class name
   */
  labelClassName?: string

  /**
   * Callback to handle the checkbox change
   */
  onChange?: (checked: boolean) => void

  /**
   * Checkbox has container
   */
  hasContainer?: boolean

  /**
   * has error
   */
  hasError?: boolean
}

/**
 * Primary UI component for user interaction
 */
const Checkbox = ({
  loading,
  checked,
  label,
  wrapperClassName,
  disabled,
  component,
  labelClassName,
  hasContainer = false,
  labelVariant = 'default',
  hasError = false,
  onChange
}: CheckboxProps) => {
  return (
    <div
      className={cn(
        'flex items-center w-fit',
        {
          'cursor-not-allowed': disabled,
          'bg-primary-light px-2 py-2.5 rounded-sm': hasContainer,
          'bg-red-light px-2 py-2.5 rounded-sm': hasError && hasContainer
        },
        wrapperClassName
      )}
      onClick={(e) => {
        if (disabled) return
        e.stopPropagation()
        onChange?.(!checked)
      }}
    >
      {loading ? (
        <Loader />
      ) : (
        <div
          className={cn(
            'w-4 h-4 min-w-4 flex items-center justify-center border border-grey-stroke rounded-xs cursor-pointer ',
            {
              'bg-primary border-primary': checked,
              'bg-transparent border-grey-stroke': !checked,
              'bg-grey-light-hover border-grey-light-hover cursor-not-allowed': disabled,
              'bg-white border border-grey-light': hasContainer && !checked
            }
          )}
        >
          {checked && <CheckMarkIcon color={tailwindColors.white.DEFAULT} />}
        </div>
      )}

      {label && !component && (
        <label
          className={cn(
            'ml-2 text-gray-700 text-sm cursor-pointer select-none',
            {
              'text-gray-400 cursor-not-allowed': disabled,
              'font-medium text-xs ': labelVariant === 'small'
            },
            labelClassName
          )}
        >
          {label}
        </label>
      )}
      {component && <div className="ml-2 cursor-pointer">{component}</div>}
    </div>
  )
}

export type GroupedCheckboxProps = {
  label: string
  options: Record<string, string>
  checkedValues: Record<string, boolean>
  onParentChange: (checked: boolean) => void
  onChildChange: (key: string, checked: boolean) => void
  errorMessage?: string
}

Checkbox.Group = ({
  label,
  options,
  checkedValues,
  errorMessage,
  onParentChange,
  onChildChange
}: GroupedCheckboxProps) => {
  const parentChecked = Object.values(checkedValues).some(Boolean)

  function handleParentChange(checked: boolean) {
    onParentChange(checked)
  }

  return (
    <div className={'border rounded-sm p-2 border-grey-light'}>
      <Checkbox label={label} checked={parentChecked} onChange={handleParentChange} />
      {errorMessage && <p className="text-red-secondary text-sm">{errorMessage}</p>}

      <div className={'border bg-white-hover border-grey-light rounded-sm p-2 mt-2'}>
        {Object.entries(options).map(([key, value]) => (
          <Checkbox
            key={key}
            label={value}
            checked={checkedValues[key] || false}
            onChange={(checked) => onChildChange(key, checked)}
          />
        ))}
      </div>
    </div>
  )
}

export default Checkbox
